/**
 * Main App Component with React Router and SEO
 *
 * Features:
 * - Code Splitting with React.lazy() for faster initial load
 * - Error Boundaries for graceful error handling
 * - Suspense for loading states during chunk loading
 * - Fully responsive and SEO-friendly setup
 */

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// ============================================================
// STATIC IMPORTS (Always needed - no code splitting)
// These components are needed on every page, so we load them upfront
// ============================================================
import Navbar from './components/NewNavbar';
import MenuAndSearchBar from './components/MenuAndSearchBar';
import Footer from './components/Footer';
import SEO from './components/SEO';
import ScrollToTop from './components/ScrollToTop';
import EnquiryPopup from './components/EnquiryPopup';
import usePopupState from './hooks/usePopupState';
import StaticNavbar from './components/StaticNavbar';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import PageLoader from './components/PageLoader';
import mixpanelService from './services/mixpanel';
import { getCookieConsent } from './utils/cookieConsent';
import './App.css';

// ============================================================
// LAZY LOADED PAGES (Code Splitting)
// These components are loaded on-demand when their route is accessed
// This reduces initial bundle size by ~40% for faster first load
// ============================================================

// Main Pages (frequently visited)
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductListingPage = lazy(() => import('./pages/ProductListingPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CategoryListing = lazy(() => import('./pages/CategoryListing'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));

// Blog Pages
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));

// Static Equipment Pages
const ForkliftsPage = lazy(() => import('./static-page/ForkliftsPage'));
const SkidSteerPage = lazy(() => import('./static-page/SkidSteerPage'));
const TowerLightPage = lazy(() => import('./static-page/TowerLightPage'));
const AirCompressorPage = lazy(() => import('./static-page/AirCompressorPage'));
const TeleHandlerPage = lazy(() => import('./static-page/TeleHandlerPage'));
const ExcavatorPage = lazy(() => import('./static-page/ExcavatorPage'));
const BackhoePage = lazy(() => import('./static-page/BackhoePage'));
const WheelLoaderPage = lazy(() => import('./static-page/WheelLoaderPage'));
const CranePage = lazy(() => import('./static-page/CranePage'));
const BulldozersPage = lazy(() => import('./static-page/BulldozersPage'));
const ArialLiftsPage = lazy(() => import('./static-page/ArialLiftsPage'));
const MiniExcavatorPage = lazy(() => import('./static-page/MiniExcavatorPage'));
const GetAQuotePage = lazy(() => import('./static-page/GetAQuotePage'));

// Other Pages
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));

// 404 Page (can be lazy loaded since it's rarely visited)
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then(module => ({ default: module.NotFoundPage }))
);

/**
 * Component to initialize analytics based on cookie consent
 * This runs once on app mount and checks if user has already given consent
 */
function AnalyticsInitializer() {
  useEffect(() => {
    // Check if user has already accepted cookies
    const consent = getCookieConsent();
    
    if (consent === "accepted") {
      try {
        mixpanelService.init?.();
        console.log("Analytics initialized - user has existing consent");
      } catch (e) {
        console.error("Mixpanel init failed:", e);
      }
    } else if (consent === "rejected") {
      console.log("Analytics disabled - user rejected cookies");
    } else {
      console.log("No cookie consent yet - waiting for user choice");
    }

    // Listen for consent changes (when user accepts/rejects from banner)
    const handleConsentChange = (event) => {
      const { consent } = event.detail;
      
      if (consent === "accepted") {
        // User just accepted - analytics already initialized in CookieConsent.jsx
        console.log("Consent accepted - analytics active");
      } else if (consent === "rejected") {
        console.log("Consent rejected - analytics will remain disabled");
      }
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  return null;
}

// Component to enforce lowercase URLs
function LowercaseRedirect({ children }) {
  const location = useLocation();
  const lowercasePath = location.pathname.toLowerCase();

  // Redirect if path contains uppercase letters
  if (location.pathname !== lowercasePath) {
    return <Navigate to={lowercasePath + location.search + location.hash} replace />;
  }

  return children;
}

// Component to conditionally render Navbar based on page type
function ConditionalNavbars() {
  const location = useLocation();

  // Define static page routes where StaticNavbar should appear
  const staticPageRoutes = [
    '/forklifts',
    '/skid-steer',
    '/lighting-towers',
    '/air-compressors',
    '/telehandlers',
    '/excavators',
    '/backhoe-loaders',
    '/wheel-loaders',
    '/all-terrain-cranes',
    '/bulldozers',
    '/arial-lifts',
    '/mini-excavator'
  ];

  // Check if current path is a static page
  const isStaticPage = staticPageRoutes.includes(location.pathname);

  return (
    <>
      {/* Regular Navbar - appears on all pages EXCEPT static pages */}
      {!isStaticPage && <Navbar  />}
      {/* Static Navbar - appears ONLY on static pages */}
      {isStaticPage && <StaticNavbar />}
    </>
  );
}

function App() {
  const { isPopupOpen, closePopup } = usePopupState({
    autoShow: true,
    delay: 2000,
    showOnce: false // Set to true if you want popup to show only once per session
  });
  const [currentProduct, setCurrentProduct] = useState(null);
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        
        {/* Initialize analytics based on existing consent */}
        <AnalyticsInitializer />
        
        <div className="App">
          {/* Conditional Navigation Bars */}
          <ConditionalNavbars product={currentProduct} />
          <MenuAndSearchBar />

          {/* Main Content Area - Wrapped with ErrorBoundary and Suspense */}
          <main id="main-content" className="min-h-screen">
            <ErrorBoundary>
              <Suspense fallback={<PageLoader message="Loading page..." />}>
                <LowercaseRedirect>
                  <Routes>
                    {/* Home Page */}
                    <Route path="/" element={
                      <ErrorBoundary>
                        <SEO
                          title="Golden Sparrow"
                          description="Find the best deals on industrial machinery. Buy, rent, or lease excavators, bulldozers, cranes, and more."
                        />
                        <HomePage />
                      </ErrorBoundary>
                    } />
              
              {/* Products Listing Page */}
              <Route path="/products" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Collections"
                    description="Browse our extensive collection of industrial equipment for sale and rent. Filter by brand, condition, and more."
                  />
                  <ProductListingPage />
                </>
              } />
              
              {/* Categories Listing Page */}
              <Route path="/categories" element={
                <>
                  <SEO 
                    title="Golden Sparrow Categories - Browse by Type"
                    description="Explore machinery categories and find exactly what you need. Browse organized listings of construction and industrial equipment."
                  />
                  <CategoryListing />
                </>
              } />
              
              {/* Product Detail Page */}
              <Route path="/product/:productSlug/:productId" element={
                <>
                  <SEO title="Golden Sparrow | Product Details" />
                  <ProductDetailPage onProductLoad={setCurrentProduct} />
                </>
              } />
              
              {/* Other Pages */}
              
              <Route path="/brands" element={
                <>
                  <SEO title="Golden Sparrow | Brands" />
                  <BrandsPage />
                </>
              } />

              {/* Categories Routes */}
              <Route path="/categories" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Categories - Buy & Rent Industrial Equipment"
                    description="Browse our comprehensive equipment categories. Find forklifts, excavators, cranes, and more industrial equipment for sale and rent."
                  />
                  <CategoriesPage />
                </>
              } />
              
              {/* Blog Routes */}
              <Route path="/blog" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Blog News & Insights"
                    description="Stay updated with the latest industry trends, equipment insights, and news in the construction and industrial equipment sector."
                  />
                  <BlogPage />
                </>
              } />
              
              <Route path="/blog/:slug" element={
                <>
                  <BlogDetailPage />
                </>
              } />
              
              {/* Static Pages */}
              <Route path="/forklifts" element={
                <>
                  <SEO 
                    title="Forklifts for Sale"
                    description=""
                  />
                  <ForkliftsPage />
                </>
              } />

              <Route path="/skid-steer" element={
                <>
                  <SEO 
                    title="Skid Steer for Sale"
                    description=""
                  />
                  <SkidSteerPage />
                </>
              } />

              <Route path="/lighting-towers" element={
                <>
                  <SEO 
                    title="Tower Lights for Sale"
                    description=""
                  />
                  <TowerLightPage />
                </>
              } />

              <Route path="/air-compressors" element={
                <>
                  <SEO 
                    title="Air Compressor for Sale"
                    description=""
                  />
                  <AirCompressorPage />
                </>
              } />

              <Route path="/telehandlers" element={
                <>
                  <SEO 
                    title="Telehandlers for Sale"
                    description=""
                  />
                  <TeleHandlerPage />
                </>
              } />

              <Route path="/excavators" element={
                <>
                  <SEO 
                    title="Excavators for Sale"
                    description=""
                  />
                  <ExcavatorPage />
                </>
              } />

              <Route path="/backhoe-loaders" element={
                <>
                  <SEO 
                    title="Backhoe Loader for Sale"
                    description=""
                  />
                  <BackhoePage />
                </>
              } />

              <Route path="/wheel-loaders" element={
                <>
                  <SEO 
                    title="Wheel Loader for Sale"
                    description=""
                  />
                  <WheelLoaderPage />
                </>
              } />

              <Route path="/all-terrain-cranes" element={
                <>
                  <SEO 
                    title="Cranes for Sale"
                    description=""
                  />
                  <CranePage />
                </>
              } />

              <Route path='/bulldozers' element={
                <>
                <SEO title='Bulldozers for Sale' description=''/>
                <BulldozersPage />
                </>
              } />

              <Route path='/arial-lifts' element={
                <>
                <SEO title='Arial Lifts for Sale' description=''/>
                <ArialLiftsPage />
                </>
              } />

              <Route path='/mini-excavator' element={<><SEO title='Mini Excavator for Sale'/> <MiniExcavatorPage /></>}/>
              <Route path='/get-a-quote' element={<><SEO title='Golden Sparrow | Get a Quote'/> <GetAQuotePage /></>}/>
              
              
              <Route path="/about-us" element={
                <>
                  <SEO title="Golden Sparrow | About Us" />
                  <AboutPage />
                </>
              } />

              <Route path='/privacy-policy' element={
                <>
                <SEO title='Golden Sparrow | Privacy Policy' />
                <PrivacyPolicy />
                </>
              } />

              <Route path='/terms-and-conditions' element={
                <>
                <SEO title='Golden Sparrow | Terms And Conditions' />
                <TermsAndConditions />
                </>
              } />

              <Route path='/delete-account' element={
                <>
                <SEO title='Golden Sparrow | Delete Account' />
                <DeleteAccount />
                </>
              } />
              
              <Route path="/contact" element={
                <>
                <SEO title='Golden Sparrow | Contact Us' />
                <ContactPage />
                </>
              } />
              
              {/* Thank You Page */}
              <Route path="/thank-you" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Thank You"
                    description="Thank you for your enquiry. Our team will contact you shortly."
                  />
                  <ThankYouPage />
                </>
              } />
              
                    {/* 404 Page */}
                    <Route path="/404" element={
                      <>
                        <SEO
                          title="Golden Sparrow | Page Not Found"
                          description="The page you're looking for doesn't exist."
                        />
                        <NotFoundPage />
                      </>
                    } />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </LowercaseRedirect>
              </Suspense>
            </ErrorBoundary>
          </main>
          
          {/* Cookie Consent Banner - Shows only if user hasn't made a choice */}
          <CookieConsent />
          
          {/* Footer */}
          <Footer product={currentProduct} />

          {/* Enquiry Popup */}
          <EnquiryPopup isOpen={isPopupOpen} onClose={closePopup} />
        </div>
      </Router>
    </HelmetProvider>
  );
}

// Loading Page Component (currently unused - kept for future use)
// function LoadingPage() {
//   return (
//     <div className="flex items-center justify-center min-h-[60vh]">
//       <div className="text-center">
//         <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
//         <p className="text-neutral-600">Loading...</p>
//       </div>
//     </div>
//   );
// }

// Coming Soon Component for pages under development (currently unused - kept for future use)
// function ComingSoonPage({ title }) {
//   return (
//     <div className="flex items-center justify-center min-h-[60vh] px-4">
//       <div className="text-center">
//         <h1 className="text-3xl sm:text-4xl font-display font-bold text-neutral-800 mb-4">{title}</h1>
//         <p className="text-neutral-600">This page is coming soon!</p>
//       </div>
//     </div>
//   );
// }

export default App;